import { createSelector } from "@reduxjs/toolkit";
import { Trans } from "@lingui/macro";

export const VOCABULARY_STATE_ROOT_SELECTOR = state => state.vocabulary;
export const CREATE_VOCABULARY_SELECTOR = state => createSelector(VOCABULARY_STATE_ROOT_SELECTOR, state);

export const SEARCH_DELAY = 300;

export const NO_MATCHES_MESSAGE = <Trans>No items that match your search</Trans>;
export const OVER_THE_LIMIT_MESSAGE = "Cannot select more than 5 items";

export const GAME_TYPES = {
  READING: "reading_game",
  SPELLING: "spelling_game",
};

export const OBJECTIVES_COLORS = [
  "#004AA1",
  "#56CC91",
  "#D95075",
  "#FCBF53",
  "#494A6D",
  "#72A3FF",
  "#217049",
  "#932542",
];

export const NUMBER_OF_SYLLABLES_OPTIONS = [
  { label: <Trans>1 syllable</Trans>, value: "1" },
  { label: <Trans>2 syllables</Trans>, value: "2" },
  { label: <Trans>3 syllables and more</Trans>, value: "3" },
  // { label: "4 syllables", value: "4" },
  // { label: "5 syllables", value: "5" },
  // { label: "6 syllables", value: "6" },
  // { label: "7 syllables", value: "7" },
  // { label: "8 syllables", value: "8" },
];

export const MAX_NUMBER_OF_SYLLABLES_OPTIONS = [
  "4", "5", "6", "7", "8",
];

export const SYLLABLES_STRUCTURE_OPTIONS = [
  { label: "V", value: "V" },
  { label: "CV", value: "CV" },
  { label: "VC", value: "VC" },
  { label: "VCC", value: "VCC" },
  { label: "CVC", value: "CVC" },
  { label: "CCVC", value: "CCVC" },
  { label: "CVCC", value: "CVCC" },
  { label: "CCV", value: "CCV" },
  { label: "CCCVC", value: "CCCVC" },
  { label: "CCVCC", value: "CCVCC" },
  { label: "CCCV", value: "CCCV" },
  { label: "CCVCCC", value: "CCVCCC" },
  { label: "CVCCC", value: "CVCCC" },
];

export const SINGLE_WORD_LIMIT = 1;

export const SOUNDS_LIST = ["sh", "z", "s", "ts", "r", "x", "m", "l", "n", "p", "v", "f", "b", "t", "d", "k", "g"]; //["ma", "ʁak", "mit", "ʁi", "ya", "t͡sa", "mid", "bam", "ba", "ko", "sem", "ʃen", "ʃa", "xoʁ", "im", "ʃim", "pan", "za", "xi", "pu", "ʃit", "miʃ", "ka", "fa", "ʁiʃ", "p", "b", "t", "d", "g", "k", "s", "z", "f", "v", "sh (ʃ)", "zh (ʒ)", "tch (tʃ)", "dj (dʒ)", "th, unvoiced (θ)", "th, voiced (ð)", "m", "n", "ŋ", "j", "w", "l", "r (ɹ)", "er (ɝ)", "r-blends", "br (bɹ)", "dr (dɹ)", "fr (fɹ)", "gr(gɹ)", "tr (tɹ)", "kr (kɹ)", "pr (pɹ)", "shr (ʃɹ)", "thr (θɹ)", "final s-blend", "ps", "ts", "ks", "s-blend (2 sound)", "sk", "sl", "sm", "sn", "sp", "st", "sw", "s-blend (3 sound)", "skw", "skr (skɹ)", "spl", "spr (spɹ)", "str (stɹ)", "l-blend", "bl", "fl", "gl", "kl", "pl", "vocalic r", "ear (iɹ)", "air (æɹ)", "are (ɑɹ)", "ore (ɔɹ)", "əɹ", "ɛɹ", "ear (ɪɹ)", "oo-r (ʊɹ)", "ire (ɑɪɹ)"];

export const SOUNDS_MAPPER = {
  sh: [
    "ʃ",
  ],
  z: [
    "z",
  ],
  s: [
    "s",
  ],
  // $ will represent the t͡s sound
  ts: [
    "$",
  ],
  r: [
    "ʁ",
  ],
  x: [
    "χ",
  ],
  m: [
    "m",
  ],
  l: [
    "l",
  ],
  n: [
    "n",
  ],
  p: [
    "p",
  ],
  v: [
    "v",
  ],
  f: [
    "f",
  ],
  b: [
    "b",
  ],
  t: [
    "t",
  ],
  d: [
    "d",
  ],
  k: [
    "k",
  ],
  g: [
    "g",
  ],
  //ma: [
  //  "ma",
  //],
  //p: [
  //  "p",
  //],
  //b: [
  //  "b",
  //],
  //t: [
  //  "t",
  //],
  //d: [
  //  "d",
  //],
  //g: [
  //  "g",
  //],
  //k: [
  //  "k",
  //],
  //s: [
  //  "s",
  //],
  //z: [
  //  "z",
  //],
  //f: [
  //  "f",
  //],
  //v: [
  //  "v",
  //],
  //"sh (ʃ)": [
  //  "ʃ",
  //],
  //"zh (ʒ)": [
  //  "ʒ",
  //],
  //"tch (tʃ)": [
  //  "tʃ",
  //],
  //"dj (dʒ)": [
  //  "dʒ",
  //],
  //"th, unvoiced (θ)": [
  //  "θ",
  //],
  //"th, voiced (ð)": [
  //  "ð",
  //],
  //m: [
  //  "m",
  //  "ma",
  //],
  //n: [
  //  "n",
  //  "ʁak",
  //  "t͡sa",
  //],
  //ŋ: [
  //  "ŋ",
  //],
  //j: [
  //  "j",
  //],
  //w: [
  //  "w",
  //],
  //l: [
  //  "l",
  //],
  //"r (ɹ)": [
  //  "ɹ",
  //],
  //"er (ɝ)": [
  //  "ɝ",
  //],
  //"r-blends": [
  //  "bɹ",
  //  "dɹ",
  //  "fɹ",
  //  "gɹ",
  //  "tɹ",
  //  "kɹ",
  //  "pɹ",
  //  "ʃɹ",
  //  "θɹ",
  //],
  //"br (bɹ)": [
  //  "bɹ",
  //],
  //"dr (dɹ)": [
  //  "dɹ",
  //],
  //"fr (fɹ)": [
  //  "fɹ",
  //],
  //"gr(gɹ)": [
  //  "gɹ",
  //],
  //"tr (tɹ)": [
  //  "tɹ",
  //],
  //"kr (kɹ)": [
  //  "kɹ",
  //],
  //"pr (pɹ)": [
  //  "pɹ",
  //],
  //"shr (ʃɹ)": [
  //  "ʃɹ",
  //],
  //"thr (θɹ)": [
  //  "θɹ",
  //],
  //"final s-blend": [
  //  "ps",
  //  "ts",
  //  "ks",
  //],
  //ps: [
  //  "ps",
  //],
  //ts: [
  //  "ts",
  //],
  //ks: [
  //  "ks",
  //],
  //"s-blend (2 sound)": [
  //  "sk",
  //  "sl",
  //  "sm",
  //  "sn",
  //  "sp",
  //  "st",
  //  "sw",
  //],
  //sk: [
  //  "sk",
  //],
  //sl: [
  //  "sl",
  //],
  //sm: [
  //  "sm",
  //],
  //sn: [
  //  "sn",
  //],
  //sp: [
  //  "sp",
  //],
  //st: [
  //  "st",
  //],
  //sw: [
  //  "sw",
  //],
  //"s-blend (3 sound)": [
  //  "skw",
  //  "skɹ",
  //  "spl",
  //  "spɹ",
  //  "stɹ",
  //],
  //skw: [
  //  "skw",
  //],
  //"skr (skɹ)": [
  //  "skɹ",
  //],
  //spl: [
  //  "spl",
  //],
  //"spr (spɹ)": [
  //  "spɹ",
  //],
  //"str (stɹ)": [
  //  "stɹ",
  //],
  //"l-blend": [
  //  "bl",
  //  "fl",
  //  "gl",
  //  "kl",
  //  "pl",
  //],
  //bl: [
  //  "bl",
  //],
  //fl: [
  //  "fl",
  //],
  //gl: [
  //  "gl",
  //],
  //kl: [
  //  "kl",
  //],
  //pl: [
  //  "pl",
  //],
  //"vocalic r": [
  //  "iɹ",
  //  "æɹ",
  //  "ɑɹ",
  //  "ɔɹ",
  //  "əɹ",
  //  "ɛɹ",
  //  "ɪɹ",
  //  "ʊɹ",
  //  "ɑɪɹ",
  //],
  //"ear (iɹ)": [
  //  "iɹ",
  //],
  //"air (æɹ)": [
  //  "æɹ",
  //],
  //"are (ɑɹ)": [
  //  "ɑɹ",
  //],
  //"ore (ɔɹ)": [
  //  "ɔɹ",
  //],
  //əɹ: [
  //  "əɹ",
  //],
  //ɛɹ: [
  //  "ɛɹ",
  //],
  //"ear (ɪɹ)": [
  //  "ɪɹ",
  //],
  //"oo-r (ʊɹ)": [
  //  "ʊɹ",
  //],
  //"ire (ɑɪɹ)": [
  //  "ɑɪɹ",
  //],
};
